import { useState, useEffect, useCallback } from 'react'
import _ from 'lodash'
import { useOrgContext } from '~/common/context/organization/org-context'
import { useCropsContext } from '~/common/context/crops/crops-context'
import { useOrgParams } from '~/common/use-org-params'
import { supabaseGetter } from '~/api/supabase-getter'

/**
 * Custom hook for fetching all crop data for any particular organization
 * Uses the caching layer for improved performance
 *
 * @returns {Object} { crops, loading, error, invalidate }
 */
export const useCropData = () => {
  const [crops, setCrops] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const { activeOrgs } = useOrgContext()
  const { refreshKey } = useCropsContext()
  const { seasonId } = useOrgParams()
  const latestActiveOrg = _.last(activeOrgs)

  const fetchPatchCrops = useCallback(async ({ patchId }) => {
    if (!patchId) return []
    const { data, error: fetchError } = seasonId
      ? await supabaseGetter.crops.getByOrganizationAndSeason(patchId, seasonId)
      : await supabaseGetter.crops.getByOrganization(patchId)

    if (fetchError) throw fetchError

    return data || []
  }, [seasonId])

  const fetchPlotCrops = useCallback(async ({ plotId }) => {
    if (!plotId) return []
    // Fetch crops attached directly to the plot
    const plotCropsResult = seasonId
      ? await supabaseGetter.crops.getByOrganizationAndSeason(plotId, seasonId)
      : await supabaseGetter.crops.getByOrganization(plotId)
    if (plotCropsResult.error) throw plotCropsResult.error

    // Get all patches in the plot
    const patchesResult = await supabaseGetter.organizations.getPatches(plotId)
    if (patchesResult.error) throw patchesResult.error

    // Fetch crops from each patch using fetchPatchCrops
    const patchCropsArrays = await Promise.all(
      patchesResult.data.filter(patch => patch.id).map(patch => fetchPatchCrops({ patchId: patch.id }))
    )

    // Combine all crops
    return [
      ...(plotCropsResult.data || []),
      ...patchCropsArrays.flat()
    ]
  }, [fetchPatchCrops, seasonId])

  const fetchFarmCrops = useCallback(async ({ farmId }) => {
    // Fetch crops attached directly to the farm
    const farmCropsResult = seasonId
      ? await supabaseGetter.crops.getByOrganizationAndSeason(farmId, seasonId)
      : await supabaseGetter.crops.getByOrganization(farmId)
    if (farmCropsResult.error) throw farmCropsResult.error

    // Get all plots in the farm
    const plotsResult = await supabaseGetter.organizations.getPlots(farmId)
    if (plotsResult.error) throw plotsResult.error

    // Fetch crops from each plot using fetchPlotCrops
    const plotCropsArrays = await Promise.all(
      plotsResult.data.filter(plot => plot.id).map(plot => fetchPlotCrops({ plotId: plot.id }))
    )

    // Combine all crops
    return [
      ...(farmCropsResult.data || []),
      ...plotCropsArrays.flat()
    ]
  }, [fetchPlotCrops, seasonId])

  useEffect(() => {
    async function fetchCrops() {
      if (!latestActiveOrg) {
        setCrops([])
        setLoading(false)
        setError(null)
        return
      }

      setLoading(true)
      setError(null)
      const { id: orgId, organization_type: orgType } = latestActiveOrg
      let data = []

      try {
        switch (orgType) {
          case 'farm': {
            data = await fetchFarmCrops({ farmId: orgId })
            break
          }
          case 'plot': {
            data = await fetchPlotCrops({ plotId: orgId })
            break
          }
          case 'patch':
          default: {
            data = await fetchPatchCrops({ patchId: orgId })
          }
        }

        // Exclude child crops (successions) — they appear as sub-rows via use-multi-crop-succession-data
        // Normalize yield: moved from crops to crop_schedules; pick the active season's schedule
        const withYield = (data || [])
          .filter(c => !c.parent_crop_id)
          .map(c => {
            const activeSchedule = c.crop_schedules?.find(s => s.season?.state === 'active')
              ?? c.crop_schedules?.[0]
            return { ...c, yield: activeSchedule?.yield ?? null }
          })
        setCrops(withYield)
      } catch (err) {
        console.error('Error fetching crops:', err)
        setError(err.message)
        setCrops([])
      } finally {
        setLoading(false)
      }
    }

    fetchCrops()
  }, [fetchPatchCrops, fetchPlotCrops, fetchFarmCrops, latestActiveOrg, refreshKey, seasonId])

  return {
    crops,
    loading,
    error,
    invalidate: () => {
      supabaseGetter.crops.invalidate()
      supabaseGetter.organizations.invalidate()
    }
  }
}
